import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  Search,
  Rocket,
  Smartphone,
  Bot,
  User,
  CreditCard,
  Shield,
  FileText,
  Wrench,
  AlertTriangle,
  MessageCircle,
  ChevronRight,
  Mail,
  Phone,
  MapPin,
  ExternalLink,
  HelpCircle
} from 'lucide-react';

const HelpCentre = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeSection, setActiveSection] = useState(null);

  const helpSections = [
    {
      id: 'getting-started',
      title: 'Getting Started',
      icon: Rocket,
      color: 'text-green-600 bg-green-100',
      items: [
        {
          question: 'What is Zeilian?',
          answer: 'Zeilian is an AI-enhanced marketplace platform designed to connect buyers and sellers. We enable the creation, discovery, verification, and distribution of listings across multiple categories including vehicles, property, electronics, services, and more. Our platform uses artificial intelligence to help users create better listings and find what they need faster.'
        },
        {
          question: 'How do I create an account?',
          answer: 'To create an account:\n\n1. Click the "Register" button in the top right corner\n2. Enter your email address and create a password\n3. Verify your email by clicking the link we send you\n4. Complete your profile with your name and contact details\n\nYou can also sign up using your Google account for faster registration.'
        },
        {
          question: 'Is Zeilian free to use?',
          answer: 'Yes! Creating an account and browsing listings is completely free. We offer free basic features including:\n\n• Creating up to 5 listings per month\n• Browsing unlimited listings\n• Contacting sellers\n• Saving favourites\n\nFor enhanced features like AI-powered ad creation and priority listings, check out our Membership Plans.'
        },
        {
          question: 'Which countries does Zeilian operate in?',
          answer: 'Zeilian currently operates in:\n\n• United Kingdom\n• Sri Lanka\n• European Union\n\nWe are continuously expanding to new regions. Each region has localised pricing, currency support, and regional features.'
        },
        {
          question: 'How do I post my first ad?',
          answer: 'Posting your first ad is easy:\n\n1. Click "Post An Ad" button\n2. Select a category for your item\n3. Add photos (up to 10 images)\n4. Write a title and description (or use our AI assistant!)\n5. Set your price\n6. Add your location\n7. Review and publish\n\nTip: Use our AI Ad Writer to create professional descriptions automatically!'
        }
      ]
    },
    {
      id: 'using-zeilian',
      title: 'Using Zeilian',
      icon: Smartphone,
      color: 'text-blue-600 bg-blue-100',
      items: [
        {
          question: 'How do I search for items?',
          answer: 'Use the search bar at the top of any page to find items. You can:\n\n• Search by keywords\n• Filter by category\n• Set location and distance radius\n• Sort by price, date, or relevance\n• Save searches for notifications\n\nAdvanced filters let you narrow down by specific attributes like condition, brand, or features.'
        },
        {
          question: 'How do I contact a seller?',
          answer: 'To contact a seller:\n\n1. Open the listing you\'re interested in\n2. Click "Contact Seller" or "Send Message"\n3. Write your message\n4. The seller will receive your message via email and in-app notification\n\nTip: Ask specific questions about the item and suggest a safe meeting place for viewing.'
        },
        {
          question: 'How do I save listings to my favourites?',
          answer: 'Click the heart icon on any listing to save it to your favourites. Access your saved listings anytime from:\n\n• My Account > Favourites\n• The heart icon in the navigation\n\nYou\'ll also receive notifications if the price drops on saved items.'
        },
        {
          question: 'How do I manage my listings?',
          answer: 'Go to My Account > My Listings to:\n\n• View all your active listings\n• Edit listing details and photos\n• Mark items as sold\n• Renew expired listings\n• Delete listings\n• View statistics and enquiries'
        },
        {
          question: 'Can I promote my listing?',
          answer: 'Yes! You can boost your listing visibility with:\n\n• Featured Listings - Appear at the top of search results\n• Highlighted Listings - Stand out with special badges\n• Bump Up - Refresh your listing date\n\nPro and Premium members get promotional features included in their plans.'
        }
      ]
    },
    {
      id: 'ai-safety',
      title: 'AI Safety & Limitations',
      icon: Bot,
      color: 'text-purple-600 bg-purple-100',
      items: [
        {
          question: 'How does Zeilian use AI?',
          answer: 'Zeilian uses AI to enhance your experience:\n\n• AI Ad Writer - Creates professional listing descriptions\n• Smart Categorisation - Suggests the best category for your item\n• Price Suggestions - Recommends competitive pricing\n• Image Enhancement - Improves photo quality\n• Search Optimisation - Better matches for your searches\n\nAll AI features are optional and you maintain full control over your content.'
        },
        {
          question: 'Is AI-generated content accurate?',
          answer: '⚠️ Important: AI-generated content may contain errors.\n\nWhile our AI tools are designed to be helpful, they:\n\n• May produce inaccurate or incomplete information\n• Should not replace professional advice\n• Require your review before publishing\n• May not reflect current market conditions\n\nAlways verify AI suggestions and edit content to ensure accuracy.'
        },
        {
          question: 'What are the limitations of AI features?',
          answer: 'AI features have the following limitations:\n\n• Cannot verify factual claims about items\n• May not understand regional specifics\n• Cannot assess item condition from photos alone\n• May suggest inappropriate categories occasionally\n• Cannot provide legal, financial, or professional advice\n\nUse AI as an assistant, not a replacement for your own judgement.'
        },
        {
          question: 'How is my data used for AI?',
          answer: 'When you use AI features:\n\n• Your input is processed to generate suggestions\n• We do not store your prompts after processing\n• AI does not learn from individual user data\n• Your personal information is not shared with AI models\n\nSee our Privacy Policy for complete data handling details.'
        },
        {
          question: 'Can I opt out of AI features?',
          answer: 'Yes, all AI features are optional. You can:\n\n• Skip AI suggestions when posting ads\n• Write your own descriptions manually\n• Disable AI recommendations in Settings\n• Choose categories yourself\n\nYour experience is fully functional without using any AI features.'
        }
      ]
    },
    {
      id: 'account-profile',
      title: 'Account & Profile',
      icon: User,
      color: 'text-orange-600 bg-orange-100',
      items: [
        {
          question: 'How do I update my profile?',
          answer: 'To update your profile:\n\n1. Go to My Account > Profile Settings\n2. Edit your name, photo, bio, and contact details\n3. Add or update your address\n4. Verify your phone number\n5. Save changes\n\nA complete profile builds trust with other users.'
        },
        {
          question: 'How do I change my password?',
          answer: 'To change your password:\n\n1. Go to My Account > Security Settings\n2. Click "Change Password"\n3. Enter your current password\n4. Enter and confirm your new password\n5. Click Save\n\nForgot your password? Use the "Forgot Password" link on the login page.'
        },
        {
          question: 'How do I verify my account?',
          answer: 'Account verification builds trust:\n\n• Email Verification - Click the link in your welcome email\n• Phone Verification - Enter the SMS code we send\n• ID Verification - Upload a government-issued ID (for business accounts)\n\nVerified accounts get a badge and increased visibility.'
        },
        {
          question: 'How do I delete my account?',
          answer: 'To delete your account:\n\n1. Go to My Account > Settings\n2. Scroll to "Delete Account"\n3. Enter your password to confirm\n4. Click "Permanently Delete Account"\n\n⚠️ This action is irreversible. All your listings, messages, and data will be permanently removed.'
        },
        {
          question: 'Can I have multiple accounts?',
          answer: 'No, each user should have only one account. Multiple accounts may be flagged and suspended. If you need separate personal and business presence, consider upgrading to a Business Account which allows you to manage both from one login.'
        }
      ]
    },
    {
      id: 'billing',
      title: 'Billing & Subscriptions',
      icon: CreditCard,
      color: 'text-emerald-600 bg-emerald-100',
      items: [
        {
          question: 'What membership plans are available?',
          answer: 'We offer flexible membership plans:\n\n• Basic - Perfect for occasional sellers (limited AI ads)\n• Pro - Best for regular sellers (more AI ads, priority support)\n• Premium - For power sellers (unlimited features) - Coming Soon!\n\nAll plans include free trials. Visit our Membership page for current pricing in your region.'
        },
        {
          question: 'How do I upgrade my plan?',
          answer: 'To upgrade your membership:\n\n1. Go to My Account > Membership\n2. View available plans\n3. Select your preferred plan\n4. Choose payment method\n5. Complete payment\n\nUpgrades take effect immediately and you\'ll be credited for any unused time on your current plan.'
        },
        {
          question: 'What payment methods are accepted?',
          answer: 'We accept:\n\n• Credit/Debit Cards (Visa, Mastercard, Amex)\n• PayPal\n• Bank Transfer (selected regions)\n• Mobile Money (Sri Lanka)\n\nAll payments are processed securely through encrypted connections.'
        },
        {
          question: 'How do I cancel my subscription?',
          answer: 'To cancel your subscription:\n\n1. Go to My Account > Membership\n2. Click "Manage Subscription"\n3. Select "Cancel Subscription"\n4. Confirm cancellation\n\nYou\'ll retain access until the end of your billing period. No refunds for partial months.'
        },
        {
          question: 'Can I get a refund?',
          answer: 'Refund policy:\n\n• 7-day money-back guarantee on first subscription\n• No refunds for partial months after trial\n• Promotional purchases are non-refundable\n• Contact support for billing errors\n\nFor refund requests, email billing@zeilian.com with your account details.'
        }
      ]
    },
    {
      id: 'privacy',
      title: 'Privacy & Data Protection',
      icon: Shield,
      color: 'text-indigo-600 bg-indigo-100',
      items: [
        {
          question: 'How is my personal data protected?',
          answer: 'We protect your data through:\n\n• SSL/TLS encryption for all connections\n• Secure password hashing\n• Regular security audits\n• GDPR and UK GDPR compliance\n• Limited employee access to personal data\n\nSee our Privacy Policy for complete details.'
        },
        {
          question: 'What data does Zeilian collect?',
          answer: 'We collect:\n\n• Account information (name, email, phone)\n• Listing content you create\n• Messages between users\n• Device and browser information\n• Usage data and analytics\n\nWe only collect what\'s necessary to provide our services.'
        },
        {
          question: 'Can I download my data?',
          answer: 'Yes! Under GDPR you have the right to your data:\n\n1. Go to My Account > Privacy\n2. Click "Download My Data"\n3. We\'ll prepare your data export\n4. You\'ll receive a download link via email\n\nData exports include all your account information, listings, and messages.'
        },
        {
          question: 'How do I manage cookie preferences?',
          answer: 'Manage cookies from the cookie banner or:\n\n1. Go to Settings > Privacy\n2. Select "Cookie Preferences"\n3. Toggle categories on/off:\n   • Essential (required)\n   • Analytics (optional)\n   • Marketing (optional)\n4. Save preferences'
        },
        {
          question: 'Is my information shared with third parties?',
          answer: 'We only share data when necessary:\n\n• Payment processors (to handle transactions)\n• Email providers (to send notifications)\n• Analytics services (anonymised data only)\n• Law enforcement (when legally required)\n\nWe never sell your personal data to advertisers.'
        }
      ]
    },
    {
      id: 'policies',
      title: 'Policies & Legal',
      icon: FileText,
      color: 'text-gray-600 bg-gray-100',
      items: [
        {
          question: 'Where can I find your Terms of Service?',
          answer: 'Our complete Terms of Service are available at:\n\n→ zeilian.com/terms\n\nKey points:\n• You must be 18+ to use Zeilian\n• Don\'t list illegal or prohibited items\n• Respect intellectual property rights\n• Be honest in your listings\n\nBy using Zeilian, you agree to these terms.'
        },
        {
          question: 'What items are prohibited?',
          answer: 'Prohibited items include:\n\n• Illegal items (drugs, weapons, stolen goods)\n• Counterfeit products\n• Adult content\n• Dangerous materials\n• Live animals (with exceptions)\n• Recalled products\n• Items violating intellectual property\n\nListings violating these policies will be removed and accounts may be suspended.'
        },
        {
          question: 'What is your copyright policy?',
          answer: 'Respect intellectual property:\n\n• Only list items you own or have rights to sell\n• Don\'t copy photos or descriptions from others\n• Don\'t use trademarked names misleadingly\n• Report IP violations to legal@zeilian.com\n\nWe respond to valid DMCA takedown requests.'
        },
        {
          question: 'How do I report a policy violation?',
          answer: 'To report violations:\n\n1. Click "Report" on the listing or profile\n2. Select the type of violation\n3. Add any supporting details\n4. Submit the report\n\nWe review all reports within 24-48 hours and take appropriate action.'
        },
        {
          question: 'Is Zeilian registered with the ICO?',
          answer: 'Yes, Zeilian is fully compliant with UK data protection laws:\n\n• Company: Zeilian Ltd\n• Company Number: 17084374\n• ICO Registration: ZC105646\n• Registered Address: 71-75 Shelton Street, Covent Garden, London, WC2H 9JQ'
        }
      ]
    },
    {
      id: 'troubleshooting',
      title: 'Troubleshooting',
      icon: Wrench,
      color: 'text-yellow-600 bg-yellow-100',
      items: [
        {
          question: 'I can\'t log into my account',
          answer: 'Try these steps:\n\n1. Check your email and password are correct\n2. Use "Forgot Password" to reset\n3. Clear browser cookies and cache\n4. Try a different browser\n5. Check if your account was suspended\n\nStill having issues? Contact support with your registered email.'
        },
        {
          question: 'My listing isn\'t showing up',
          answer: 'Listings may not appear if:\n\n• They\'re pending review (first-time posters)\n• They violated our policies\n• There\'s a technical delay (wait 15 minutes)\n• The listing expired\n\nCheck My Listings for the status. Contact support if it\'s been over 24 hours.'
        },
        {
          question: 'I\'m not receiving emails',
          answer: 'If you\'re missing emails:\n\n1. Check your spam/junk folder\n2. Add noreply@zeilian.com to contacts\n3. Verify your email address in settings\n4. Check email preferences aren\'t turned off\n5. Try a different email address\n\nNote: Some email providers delay delivery.'
        },
        {
          question: 'Images won\'t upload',
          answer: 'Image upload requirements:\n\n• Maximum 10MB per image\n• Formats: JPG, PNG, WebP\n• Minimum 300x300 pixels\n• Maximum 10 images per listing\n\nTry:\n• Reducing image size\n• Using a different format\n• Checking your internet connection\n• Clearing browser cache'
        },
        {
          question: 'The website is loading slowly',
          answer: 'To improve performance:\n\n1. Check your internet connection\n2. Clear browser cache and cookies\n3. Disable browser extensions\n4. Try a different browser\n5. Update your browser to latest version\n\nIf issues persist, we may be experiencing high traffic. Try again later.'
        }
      ]
    },
    {
      id: 'safety',
      title: 'Safety & Reporting',
      icon: AlertTriangle,
      color: 'text-red-600 bg-red-100',
      items: [
        {
          question: 'How do I stay safe when buying?',
          answer: 'Safety tips for buyers:\n\n• Meet in public places during daylight\n• Bring someone with you\n• Inspect items thoroughly before paying\n• Don\'t send money in advance\n• Use secure payment methods\n• Trust your instincts - if it feels wrong, walk away\n• Never share personal/financial details'
        },
        {
          question: 'How do I stay safe when selling?',
          answer: 'Safety tips for sellers:\n\n• Meet in public, well-lit areas\n• Don\'t invite strangers to your home\n• Accept cash or secure payments only\n• Be wary of overpayment scams\n• Don\'t ship before payment clears\n• Keep records of all transactions\n• Report suspicious buyers'
        },
        {
          question: 'How do I recognise a scam?',
          answer: 'Warning signs of scams:\n\n• Price too good to be true\n• Seller asks for unusual payment methods\n• Pressure to act quickly\n• Requests for personal information\n• Offering to overpay and refund difference\n• Refusing to meet in person\n• Poor grammar/generic messages\n\nWhen in doubt, don\'t proceed.'
        },
        {
          question: 'How do I report a suspicious user?',
          answer: 'To report suspicious activity:\n\n1. Go to the user\'s profile or listing\n2. Click the "Report" button\n3. Select the reason (scam, harassment, etc.)\n4. Provide details and evidence\n5. Submit the report\n\nFor urgent safety concerns, also contact local authorities.'
        },
        {
          question: 'What happens when I report someone?',
          answer: 'When you submit a report:\n\n1. Our Trust & Safety team reviews it (24-48 hours)\n2. We may contact you for more information\n3. Appropriate action is taken (warning, suspension, ban)\n4. You\'ll be notified of the outcome\n5. Serious cases reported to authorities\n\nReports are confidential - the reported user won\'t know who reported them.'
        }
      ]
    },
    {
      id: 'contact',
      title: 'Contact Support',
      icon: MessageCircle,
      color: 'text-teal-600 bg-teal-100',
      items: [
        {
          question: 'How do I contact customer support?',
          answer: 'Reach us through:\n\n📧 Email: support@zeilian.com\n⏰ Response time: Within 24 hours\n\n📧 Privacy queries: privacy@zeilian.com\n📧 Legal matters: legal@zeilian.com\n📧 Billing issues: billing@zeilian.com\n\nPro and Premium members get priority support.'
        },
        {
          question: 'What are your support hours?',
          answer: 'Support availability:\n\n• Email: 24/7 (response within 24 hours)\n• Live Chat: Coming soon!\n\nUK Hours: Monday-Friday 9am-6pm GMT\nSri Lanka Hours: Monday-Friday 9am-6pm IST\n\nEmergency reports are monitored outside these hours.'
        },
        {
          question: 'How long does it take to get a response?',
          answer: 'Response times:\n\n• General enquiries: 24-48 hours\n• Billing issues: 24 hours\n• Safety reports: 24 hours (urgent: faster)\n• Technical issues: 24-48 hours\n• Pro/Premium members: Priority (12 hours)\n\nComplex issues may take longer to fully resolve.'
        },
        {
          question: 'Can I call customer support?',
          answer: 'Phone support is currently not available. We find that email allows us to:\n\n• Provide detailed, accurate responses\n• Keep a record for your reference\n• Escalate to specialists when needed\n• Respond faster to more users\n\nPhone support may be added for Premium members in the future.'
        },
        {
          question: 'Where is Zeilian located?',
          answer: 'Zeilian Ltd\n\n📍 Registered Office:\n71-75 Shelton Street\nCovent Garden\nLondon, WC2H 9JQ\nUnited Kingdom\n\n📍 Sri Lanka Office:\nLewis Place, Negombo\nGampaha District, Sri Lanka\n\nCompany No: 17084374\nICO Registration: ZC105646'
        }
      ]
    }
  ];

  // Filter sections based on search
  const filteredSections = helpSections.map(section => ({
    ...section,
    items: section.items.filter(item => 
      searchQuery === '' ||
      item.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.answer.toLowerCase().includes(searchQuery.toLowerCase())
    )
  })).filter(section => section.items.length > 0);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-blue-600 via-purple-600 to-indigo-700 text-white py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <HelpCircle className="h-16 w-16 mx-auto mb-4 opacity-80" />
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Help Centre</h1>
          <p className="text-xl text-blue-100 mb-8">
            Find answers to your questions about using Zeilian
          </p>
          
          {/* Search Bar */}
          <div className="max-w-xl mx-auto relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <Input
              type="text"
              placeholder="Search for help..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12 pr-4 py-6 text-lg text-gray-900 rounded-xl border-0 shadow-lg"
            />
          </div>
        </div>
      </div>

      {/* Quick Links */}
      <div className="max-w-6xl mx-auto px-4 -mt-8">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {helpSections.slice(0, 5).map((section) => {
            const Icon = section.icon;
            return (
              <Card 
                key={section.id}
                className="cursor-pointer hover:shadow-lg transition-shadow bg-white"
                onClick={() => {
                  setActiveSection(section.id);
                  document.getElementById(section.id)?.scrollIntoView({ behavior: 'smooth' });
                }}
              >
                <CardContent className="p-4 text-center">
                  <div className={`w-12 h-12 mx-auto mb-2 rounded-xl flex items-center justify-center ${section.color}`}>
                    <Icon className="h-6 w-6" />
                  </div>
                  <p className="text-sm font-medium text-gray-700">{section.title}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        {searchQuery && (
          <p className="text-gray-600 mb-6">
            Found {filteredSections.reduce((acc, s) => acc + s.items.length, 0)} results for "{searchQuery}"
          </p>
        )}

        {filteredSections.map((section) => {
          const Icon = section.icon;
          return (
            <div key={section.id} id={section.id} className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${section.color}`}>
                  <Icon className="h-5 w-5" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">{section.title}</h2>
              </div>
              
              <Card>
                <CardContent className="p-0">
                  <Accordion type="single" collapsible className="w-full">
                    {section.items.map((item, index) => (
                      <AccordionItem key={index} value={`${section.id}-${index}`} className="border-b last:border-0">
                        <AccordionTrigger className="px-6 py-4 text-left hover:no-underline hover:bg-gray-50">
                          <span className="font-medium text-gray-900">{item.question}</span>
                        </AccordionTrigger>
                        <AccordionContent className="px-6 pb-4">
                          <div className="text-gray-600 whitespace-pre-line leading-relaxed">
                            {item.answer}
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </CardContent>
              </Card>
            </div>
          );
        })}

        {filteredSections.length === 0 && (
          <div className="text-center py-12">
            <HelpCircle className="h-16 w-16 mx-auto text-gray-300 mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">No results found</h3>
            <p className="text-gray-500 mb-4">Try a different search term or browse the categories below</p>
            <Button onClick={() => setSearchQuery('')}>Clear Search</Button>
          </div>
        )}

        {/* Contact Card */}
        <Card className="mt-12 bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
          <CardContent className="p-8">
            <div className="text-center mb-6">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Still need help?</h3>
              <p className="text-gray-600">Our support team is here to assist you</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-blue-100 flex items-center justify-center">
                  <Mail className="h-6 w-6 text-blue-600" />
                </div>
                <h4 className="font-semibold mb-1">Email Us</h4>
                <a href="mailto:support@zeilian.com" className="text-blue-600 hover:underline">
                  support@zeilian.com
                </a>
              </div>
              
              <div className="text-center">
                <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-green-100 flex items-center justify-center">
                  <MapPin className="h-6 w-6 text-green-600" />
                </div>
                <h4 className="font-semibold mb-1">Our Address</h4>
                <p className="text-sm text-gray-600">71-75 Shelton Street<br/>London, WC2H 9JQ</p>
              </div>
              
              <div className="text-center">
                <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-purple-100 flex items-center justify-center">
                  <FileText className="h-6 w-6 text-purple-600" />
                </div>
                <h4 className="font-semibold mb-1">Legal</h4>
                <div className="text-sm text-gray-600">
                  <Link to="/terms" className="text-blue-600 hover:underline block">Terms of Service</Link>
                  <Link to="/privacy" className="text-blue-600 hover:underline block">Privacy Policy</Link>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default HelpCentre;
